import React, { useState } from "react";
import { Dropdown, Menu, message } from "antd";
import "./downloadMenu.css";

/**
 * The download button: one control, four formats.
 *
 * PDF, PNG and XLS are always offered - they are the ways a reader takes the numbers in
 * front of them away, and that is not something a board's author should be able to switch
 * off. CSV is the exception: it is the one format behind the editor's "csvdownload"
 * property, which is what `allowCsv` carries.
 *
 * It appears in two places, and they are exclusive:
 *   - in a widget's header, on a board holding several widgets, and
 *   - in the page header, on a board holding one - there the widget draws no header of
 *     its own, so this is the only place left to put it. A report is always that case.
 *
 * The work is done by the caller through onSelect(format), which returns a promise; this
 * only reports on it. Formats are blocking (jsPDF and dom-to-image both work
 * synchronously once they start), so the button says "Preparing" and locks rather than
 * looking dead for the second or two a large table takes.
 */
const FORMATS = [
  { key: "pdf", label: "PDF" },
  { key: "png", label: "PNG image" },
  { key: "xlsx", label: "Excel (XLS)" },
  { key: "csv", label: "CSV" },
];

const DownloadIcon = (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" width="15" height="15">
    <path d="M8 2v8m0 0L5 7m3 3l3-3" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M2.5 11.5v1a1.5 1.5 0 001.5 1.5h8a1.5 1.5 0 001.5-1.5v-1" strokeLinecap="round" />
  </svg>
);

const SpinnerIcon = (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" width="15" height="15">
    <circle cx="8" cy="8" r="6" opacity="0.25" />
    <path d="M14 8a6 6 0 00-6-6" strokeLinecap="round">
      <animateTransform
        attributeName="transform"
        type="rotate"
        from="0 8 8"
        to="360 8 8"
        dur="0.8s"
        repeatCount="indefinite"
      />
    </path>
  </svg>
);

export default function DownloadMenu({ allowCsv, onSelect, className, disabled, title = "Download" }) {
  const [busy, setBusy] = useState(null);

  const formats = FORMATS.filter((f) => f.key !== "csv" || allowCsv);

  const handleSelect = async ({ key }) => {
    if (busy) return;
    setBusy(key);
    /* duration 0 means it stays until dismissed, and antd 4 hands back the function that
       dismisses it. message.destroy(key) is not that function here - in this version
       destroy() takes no argument and would clear every message on screen, including one
       another part of the page had just put up. */
    const dismiss = message.loading({ content: "Preparing your download…", duration: 0 });

    try {
      const result = await onSelect(key);
      /* The formats answer rather than throw when there is nothing to write, so the
         reason can be specific: an empty table and a table too big to photograph are
         different problems and used to read the same. "handled" means the caller has
         already said something more useful than anything that could be said here. */
      if (result === "too-many-rows") {
        // a picture has a size limit; the other three formats do not
        message.warning("This is too large for an image — PDF, Excel or CSV will hold every row");
      } else if (result?.files > 1) {
        /* A table taller than one picture is split across several. Said out loud, because
           several files arriving from one click is otherwise alarming. */
        message.success(`Saved as ${result.files} images — the rows continue in order`);
      } else if (!result) {
        message.warning("There is no data to download for this widget");
      }
    } catch (error) {
      console.error("Widget export failed:", error);
      message.error("Sorry, that download could not be prepared");
    } finally {
      if (typeof dismiss === "function") dismiss();
      setBusy(null);
    }
  };

  const menu = (
    <Menu onClick={handleSelect} className="download-menu-list">
      {formats.map((f) => (
        <Menu.Item key={f.key} disabled={Boolean(busy)}>
          {busy === f.key ? "Preparing…" : f.label}
        </Menu.Item>
      ))}
    </Menu>
  );

  /* `disabled` is the caller's - the board view holds it down while the widgets are still
     fetching, because there are no rows to write yet. `busy` is this component's own, for
     the seconds a format takes to build. Either one closes the menu. */
  const locked = Boolean(busy) || Boolean(disabled);

  return (
    <Dropdown overlay={menu} trigger={["click"]} placement="bottomRight" disabled={locked}>
      <button
        type="button"
        className={className}
        title={title}
        disabled={locked}
        onClick={(e) => e.preventDefault()}
      >
        {busy ? SpinnerIcon : DownloadIcon}
      </button>
    </Dropdown>
  );
}
